(() => {
  const root = document.querySelector('[data-lifty-popups]');
  if (!root || root.dataset.designMode === 'true') return;

  const qs = (selector, scope = root) => scope.querySelector(selector);
  const qsa = (selector, scope = root) => [...scope.querySelectorAll(selector)];
  const truthy = (value) => value === 'true';
  const number = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
  const session = window.sessionStorage;
  const local = window.localStorage;
  const preview = new URLSearchParams(location.search).get('lifty-popup-preview');
  const backdrop = qs('[data-lifty-popup-backdrop]');
  const popups = new Map(qsa('[data-lifty-popup]').map((popup) => [popup.dataset.liftyPopup, popup]));
  const queue = [];
  const shown = new Set();
  let active = null;
  let opener = null;
  let lastClosedAt = 0;
  let privateProductId = null;
  let privateLineKey = null;

  const country = () => (root.dataset.country || '').toUpperCase();
  const province = () => (root.dataset.province || '').toUpperCase();
  const isIreland = () => country() === 'IE';
  const isNorthernIreland = () => country() === 'GB' && ['NIR', 'NI', 'NORTHERN IRELAND'].includes(province());
  const isGreatBritain = () => country() === 'GB' && !isNorthernIreland();
  const analyticsAllowed = () => {
    try {
      return !window.Shopify?.customerPrivacy?.analyticsProcessingAllowed ||
        window.Shopify.customerPrivacy.analyticsProcessingAllowed();
    } catch (_) {
      return false;
    }
  };

  const track = (eventName, popupId, detail = {}) => {
    const payload = {
      event: eventName,
      popup_id: popupId,
      market: country(),
      page_type: root.dataset.pageType,
      product_id: root.dataset.productId || undefined,
      cart_value: number(root.dataset.liftyCartValue),
      trigger_type: detail.trigger_type,
      ...detail
    };
    document.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
    if (analyticsAllowed() && Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
  };

  const focusable = (popup) => qsa(
    'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    popup
  ).filter((element) => !element.hidden && element.offsetParent !== null);

  const storageKey = (id, suffix = 'shown') => `lifty_popup_${id}_${suffix}`;

  const eligibleByFrequency = (id) => {
    if (preview === id) return true;
    if (session.getItem(storageKey(id)) === '1') return false;
    const until = number(local.getItem(storageKey(id, 'dismissed_until')));
    return until <= Date.now();
  };

  const close = (reason = 'close', cooldownDays = 0) => {
    if (!active) return;
    const id = active.dataset.liftyPopup;
    active.hidden = true;
    backdrop.hidden = true;
    document.documentElement.classList.remove('lifty-popup-open');
    if (cooldownDays > 0) {
      local.setItem(storageKey(id, 'dismissed_until'), String(Date.now() + cooldownDays * 86400000));
    }
    track('lifty_popup_close', id, { trigger_type: reason });
    active = null;
    lastClosedAt = Date.now();
    if (opener?.focus) opener.focus();
    opener = null;
    setTimeout(processQueue, number(root.dataset.minimumGap, 30000));
  };

  const open = (id, trigger = 'automatic') => {
    const popup = popups.get(id);
    if (!popup || active || (!preview && !eligibleByFrequency(id))) return false;
    opener = document.activeElement;
    active = popup;
    popup.hidden = false;
    backdrop.hidden = false;
    document.documentElement.classList.add('lifty-popup-open');
    session.setItem(storageKey(id), '1');
    shown.add(id);
    requestAnimationFrame(() => {
      const target = qs('[data-lifty-close]', popup) || popup;
      target.focus();
    });
    track('lifty_popup_view', id, {
      trigger_type: trigger,
      session_display_count: shown.size,
      product_compliance_classification: id === 'private-land' ? 'ireland-private-land-only' : undefined
    });
    return true;
  };

  function processQueue() {
    if (active || !queue.length) return;
    const wait = Math.max(0, number(root.dataset.minimumGap, 30000) - (Date.now() - lastClosedAt));
    if (wait) {
      setTimeout(processQueue, wait);
      return;
    }
    queue.sort((a, b) => a.priority - b.priority);
    const next = queue.shift();
    open(next.id, next.trigger);
  }

  const enqueue = (id, trigger = 'automatic') => {
    const popup = popups.get(id);
    if (!popup || (!preview && (!eligibleByFrequency(id) || queue.some((item) => item.id === id)))) return;
    queue.push({ id, trigger, priority: number(popup.dataset.priority, 99) });
    processQueue();
  };

  qsa('[data-lifty-close]').forEach((button) => button.addEventListener('click', () => {
    const id = active?.dataset.liftyPopup;
    const cooldown = id === 'regulation'
      ? number(root.dataset.regulationCooldown)
      : id === 'exit'
        ? number(root.dataset.exitCooldown, 7)
        : 0;
    close('dismissed', cooldown);
  }));

  backdrop?.addEventListener('click', () => {
    const id = active?.dataset.liftyPopup;
    if (id === 'private-land') return;
    close('backdrop');
  });

  document.addEventListener('keydown', (event) => {
    if (!active) return;
    if (event.key === 'Escape') {
      close('escape');
      return;
    }
    if (event.key !== 'Tab') return;
    const items = focusable(active);
    if (!items.length) {
      event.preventDefault();
      active.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  const privateCheck = qs('[data-lifty-private-check]');
  const privateContinue = qs('[data-lifty-private-continue]');
  privateCheck?.addEventListener('change', () => {
    privateContinue.disabled = !privateCheck.checked;
  });
  privateContinue?.addEventListener('click', () => {
    if (!privateCheck.checked) return;
    session.setItem(`lifty_private_land_ack_${privateProductId || root.dataset.productId}`, '1');
    try {
      fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { private_land_warning_acknowledged: 'true' } })
      });
    } catch (_) {}
    track('lifty_regulation_acknowledged', 'private-land');
    close('acknowledged');
  });

  qs('[data-lifty-private-remove]')?.addEventListener('click', async () => {
    try {
      let key = privateLineKey;
      if (!key) {
        const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((response) => response.json());
        const line = cart.items.find((item) => String(item.product_id) === String(privateProductId));
        key = line?.key;
      }
      if (key) {
        await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        track('lifty_private_land_product_removed', 'private-land', { product_id: privateProductId });
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
      close('product_removed');
    } catch (_) {
      close('remove_failed');
    }
  });

  qs('[data-lifty-btw-apply]')?.addEventListener('click', (event) => {
    const link = event.currentTarget;
    const url = new URL(link.href, location.origin);
    if (root.dataset.productId) url.searchParams.set('product_id', root.dataset.productId);
    if (root.dataset.variantId) url.searchParams.set('variant_id', root.dataset.variantId);
    link.href = url.toString();
    track('lifty_bike_to_work_click', 'btw');
  });

  qs('[data-lifty-repair-book]')?.addEventListener('click', () => {
    const anchor = (root.dataset.repairAnchor || 'repair-booking').replace(/^#/, '');
    const target = document.getElementById(anchor);
    track('lifty_repair_booking_click', 'repair');
    close('booking_click');
    if (target) {
      target.setAttribute('tabindex', target.getAttribute('tabindex') || '-1');
      target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      target.focus({ preventScroll: true });
    } else if (root.dataset.repairUrl) {
      location.href = root.dataset.repairUrl;
    }
  });

  qs('[data-lifty-return-cart]')?.addEventListener('click', () => {
    close('return_to_order');
    if (window.T4SThemeSP && window.T4SThemeSP.Drawer && (window.jQuery_T4NT || window.jQuery)) {
      try { window.T4SThemeSP.Drawer.opend((window.jQuery_T4NT || window.jQuery)('#t4s-mini_cart')); return; } catch (e) {}
    }
    document.dispatchEvent(new CustomEvent('cart:refresh:opend'));
  });

  qs('.lifty-popup__form')?.addEventListener('submit', () => {
    track('lifty_popup_form_submit', 'exit');
  });

  const productIsScooter = truthy(root.dataset.productIsScooter);
  const productIsEbike = truthy(root.dataset.productIsEbike);
  const productPrivateLand = truthy(root.dataset.productPrivateLand);
  const productBtw = truthy(root.dataset.productBtw);
  const collectionIsScooter = (root.dataset.collectionHandle || '').includes('scooter');
  const pageIsHome = root.dataset.pageType === 'index';

  if (preview && popups.has(preview)) {
    open(preview, 'preview');
    return;
  }

  if (
    truthy(root.dataset.regulationEnabled) &&
    isIreland() &&
    (pageIsHome || productIsScooter || collectionIsScooter)
  ) {
    setTimeout(() => enqueue('regulation', 'time_on_scooter_page'), number(root.dataset.regulationDelay, 10000));
  }

  if (truthy(root.dataset.ukEnabled) && isGreatBritain() && number(root.dataset.liftyCartCount) > 0) {
    setTimeout(() => enqueue('uk', 'great_britain_cart'), 2500);
  }

  if (truthy(root.dataset.btwEnabled) && isIreland() && productIsEbike && productBtw) {
    setTimeout(() => enqueue('btw', 'eligible_ebike_page'), number(root.dataset.btwDelay, 10000));
  }

  if (truthy(root.dataset.repairEnabled) && truthy(root.dataset.repairPage)) {
    setTimeout(() => enqueue('repair', 'repair_page_help'), number(root.dataset.repairDelay, 10000));
  }

  const inspectAddedProduct = async (payload) => {
    const productId = payload?.product_id || payload?.id;
    const handle = payload?.handle;
    if (!productId && !handle) return;
    let tags = '';
    try {
      if (handle) {
        const productData = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`).then((response) => response.json());
        tags = Array.isArray(productData.tags) ? productData.tags.join('|').toLowerCase() : String(productData.tags || '').toLowerCase();
      }
    } catch (_) {}
    const qualifyingTags = (root.dataset.privateTags || '').split('|').map((tag) => tag.trim()).filter(Boolean);
    const tagMatch = qualifyingTags.some((tag) => tags.includes(tag));
    const isCurrentPrivate = String(productId) === String(root.dataset.productId) && productPrivateLand;
    if (
      truthy(root.dataset.privateEnabled) &&
      isIreland() &&
      (tagMatch || isCurrentPrivate) &&
      session.getItem(`lifty_private_land_ack_${productId}`) !== '1'
    ) {
      privateProductId = productId;
      privateLineKey = payload?.key || null;
      setTimeout(() => enqueue('private-land', 'qualifying_cart_add'), number(root.dataset.privateDelay, 5000));
    }
  };

  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
    if (/\/cart\/add(?:\.js)?(?:\?|$)/.test(url) && response.ok) {
      response.clone().json().then((data) => {
        const item = Array.isArray(data?.items) ? data.items[0] : data;
        inspectAddedProduct(item);
        if (truthy(root.dataset.btwEnabled) && isIreland() && productIsEbike && productBtw) {
          setTimeout(() => enqueue('btw', 'eligible_ebike_cart_add'), 2000);
        }
      }).catch(() => {});
    }
    return response;
  };

  if (truthy(root.dataset.exitEnabled) && number(root.dataset.liftyCartCount) > 0) {
    const triggerExit = (type) => enqueue('exit', type);
    if (matchMedia('(pointer:fine)').matches) {
      document.addEventListener('mouseout', (event) => {
        if (!event.relatedTarget && event.clientY <= 8) triggerExit('desktop_exit_intent');
      }, { passive: true });
    } else {
      let inactivityTimer;
      let lastScrollY = window.scrollY;
      let engaged = false;
      const resetInactivity = () => {
        clearTimeout(inactivityTimer);
        inactivityTimer = setTimeout(() => {
          if (engaged) triggerExit('mobile_inactivity');
        }, number(root.dataset.exitInactivity, 45000));
      };
      ['touchstart', 'click', 'keydown'].forEach((eventName) => document.addEventListener(eventName, () => {
        engaged = true;
        resetInactivity();
      }, { passive: true }));
      window.addEventListener('scroll', () => {
        if (window.scrollY < lastScrollY - 160 && engaged) triggerExit('mobile_upward_scroll');
        lastScrollY = window.scrollY;
        resetInactivity();
      }, { passive: true });
      resetInactivity();
    }
  }

  document.addEventListener('shopify:localizationchange', () => location.reload());
})();
